<template>
    <div class="modal">
        <div class="modal-container">
            <div class="modal-header">
                <close-icon v-on:click="closeView"></close-icon>
                <h1>New Product Order</h1>
            </div>

            <div class="modal-body no-pad">
                <div class="flex h-full">
                    <div class="w-full md:w-1/2 md:pr-4">
                        <div class="account-body">
                            <div class="form-field select-none">
                                <label class="mr-4" style="display: inline-block;">
                                    <input type="radio" name="product" value="Product" v-model="orderType">
                                    Product
                                </label>

                                <label style="display: inline-block;">
                                    <input type="radio" name="product" value="VariableProduct" v-model="orderType">
                                    Variable Product
                                </label>
                            </div>

                            <div class="form-field" v-if="isProductOrder">
                                <label>Product</label>
                                <select v-model="productOrderProduct">
                                    <option v-for="product in products" :value="product">{{ product.name }}</option>
                                </select>
                            </div>

                            <div class="form-field" v-if="isProductOrder && productOrderProduct">
                                <label>Quantity</label>
                                <input type="text" v-model="productOrderQuantity" placeholder="Enter quantity of product to apply">
                                <span class="text-sm text-gray">Enter the quantity of credits for this product to apply to the account</span>
                            </div>

                            <div class="form-field" v-if="isVariableOrder">
                                <label>Order Description</label>
                                <input type="text" v-model="variableOrderName" placeholder="Enter order descriptive text">
                            </div>

                            <div class="form-field" v-if="isVariableOrder || productOrderProduct">
                                <label>Order Gross</label>
                                <input type="text" v-model="orderAmount" placeholder="0.00">
                                <span class="text-sm text-gray">Enter the gross total amount to charge, including any applicable VAT. Tax will be automatically calculated.</span>
                            </div>

                            <div class="form-field" v-if="isProductOrder && productOrderProduct">
                                <label>Payment Method</label>
                                <select v-model="paymentMethod">
                                    <option value="card">Credit Card</option>
                                    <option value="invoice">Invoice</option>
                                    <option value="user-checkout">User Checkout</option>
                                </select>
                            </div>

                            <div class="form-field" v-if="isProductOrder && productOrderProduct">
                                <label>VAT Rate</label>
                                <TaxRateSelect v-model="taxRate" />
                            </div>
                        </div>
                    </div>

                    <div class="w-full md:w-1/2 md:pr-4">
                        <div class="account-body">

                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <div class="flex">
                    <div class="left text-left">
                        <div class="left text-left">
                            <a href="javascript:void(0)" @click.prevent="changingAttribution = true" style="line-height: 42px;" v-if="!changingAttribution">Sold by: {{ currentUserEmail }}</a>
                            <select v-model="selectedAttribution" v-if="changingAttribution">
                                <option v-for="(user, id) in users" :value="user.id">{{ user.email }}</option>
                            </select>
                        </div>
                    </div>

                    <div class="right text-right">
                        <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>

                        <button
                            class="btn-primary"
                            type="button"
                            @click="submitOrder"
                            v-if="shouldShowProcessButton"
                            :disabled="!shouldShowProcessButton"
                        >Process Order</button>

                        <card-payment
                            :amount="parseFloat(orderAmount)"
                            :saved="savedBilling"
                            :stripeKey="stripeKey"
                            text="Process Order"
                            title="Card Payment"
                            @submit="handleCardPayment"
                            v-if="shouldShowCardPaymentButton"
                        ></card-payment>

                        <invoice-payment
                            :amount="parseFloat(orderAmount)"
                            :account="account"
                            text="Create Invoice"
                            title="Invoice Payment"
                            @submit="handleInvoicePayment"
                            v-if="shouldShowInvoicePaymentButton"
                        ></invoice-payment>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import CardPayment from '../../CardPayment'
    import InvoicePayment from '../../InvoicePayment'
    import TaxRateSelect from '../../TaxRateSelect'

    export default {
        name: 'OrderCreate',
        props: {
            account: {
                type: Object,
            },
        },
        components: {
            TaxRateSelect,
            CardPayment,
            InvoicePayment,
        },
        data() {
            return {
                products: [],
                orderType: 'Product',
                productOrderProduct: null,
                productOrderQuantity: 1,
                variableOrderName: null,
                orderAmount: null,
                changingAttribution: false,
                selectedAttribution: null,
                users: [],
                stripeKey: null,
                paymentMethod: 'card',
                paymentToken: null,
                taxRate: null,
            }
        },
        mounted() {
            this.stripeKey = document.querySelector('meta[name="stripe-key"]').getAttribute('content')

            this.setMetaTitle('Create Order | JournoLink Control')

            window.addEventListener('keyup', this.handleKeypress)

            this.selectedAttribution = window.Laravel.auth.user.id
            this.taxRate = this.getDefaultTaxRate()

            this.loadData()
        },
        beforeDestroy() {
            window.removeEventListener('keyup', this.handleKeypress)
        },
        watch: {
            /**
             * React when the product value is changed and set the amount due
             *
             * @param   {Object}  newValue
             * @param   {Object}  oldValue
             * @return  {void}
             */
            productOrderProduct (newValue, oldValue) {
                this.orderAmount = (this.formatCurrency(newValue.cost * (1 + this.taxRate.rate)) * (this.productOrderQuantity || 1)).toFixed(2)
            },
            /**
             * React when the quantity value is changed and set the amount due
             *
             * @param   {Number}  newValue
             * @param   {Number}  oldValue
             * @return  {void}
             */
            productOrderQuantity (newValue, oldValue) {
                this.orderAmount = (this.formatCurrency(this.productOrderProduct.cost * (1 + this.taxRate.rate)) * (this.productOrderQuantity || 1)).toFixed(2)
            },
            /**
             * React when the tax code value changes.
             *
             * @return {void}
             */
            taxRate() {
                if (this.productOrderProduct) {
                    this.orderAmount = (this.formatCurrency(this.productOrderProduct.cost * (1 + this.taxRate.rate)) * (this.productOrderQuantity || 1)).toFixed(2)
                }
            },
        },
        computed: {
            /**
             * Determine if this is selected as a Product based order
             *
             * @return  {Boolean}
             */
            isProductOrder() {
                return this.orderType === 'Product'
            },
            /**
             * Determine if this is selected as a Product based order
             *
             * @return  {Boolean}
             */
            isVariableOrder() {
                return this.orderType === 'VariableProduct'
            },
            /**
             * Determine if the "Save" button should be shown
             *
             * @return {Boolean}
             */
            shouldShowProcessButton() {
                return (this.orderAmount !== null && this.orderAmount !== '' && parseInt(this.orderAmount) === 0) || this.paymentMethod === 'user-checkout'
            },
            /**
             * Determine if the "Process Payment" button should be shown
             *
             * @return {Boolean}
             */
            shouldShowCardPaymentButton() {
                return this.orderAmount !== null && this.orderAmount !== '' && this.orderAmount > 0 && this.paymentMethod === 'card'
            },
            /**
             * Determine if the "Create Invoice" button should be shown.
             *
             * @return {Boolean}
             */
            shouldShowInvoicePaymentButton() {
                return this.orderAmount !== null && this.orderAmount !== '' && this.orderAmount > 0 && this.paymentMethod === 'invoice'
            },
            /**
             * The current authenticated user email address
             *
             * @return {String}
             */
            currentUserEmail() {
                return window.Laravel.auth.user.email
            },
            /**
             * Return the selected product ID
             *
             * @return  {Number}
             */
            selectedProductId() {
                if (this.orderType === 'VariableProduct') {
                    return null
                }

                if (this.productOrderProduct === null) {
                    return null;
                }

                return this.productOrderProduct.id
            },
            /**
             * Return the saved billing agreements
             *
             * @return {Array}
             */
            savedBilling() {
                let billing = this.account?.subscription?.order?.order_billing

                if (!billing || !billing.provider || !billing.recurring_token || billing.recurring_token.length === 0) {
                    return []
                }

                return [billing]
            },
        },
        methods: {
            loadData() {
                axios.all([
                    axios.get(route('products')),
                    axios.get(route('users.list', {term: 'type:admin'})),
                ])
                    .then(axios.spread((productsResponse, usersResponse) => {
                        this.products = productsResponse.data.results.data
                        this.users = usersResponse.data.results.data
                    }))
                    .catch(e => {
                        alert('API Error')
                        console.error(e)
                    })
            },
            /**
             * Emit the close event to the parent
             *
             * @return {void}
             */
            closeView() {
                this.$root.goBack()
            },
            /**
             * Format currency value for output
             *
             * @param  {Float} value
             * @return {String}
             */
            formatCurrency(value) {
                if (value < 0) {
                    return '(' + Math.abs(value).toFixed(2) + ')'
                }

                return Math.abs(value).toFixed(2)
            },
            /**
             * Handle a payment made by a card provider.
             *
             * @param  {Object} token
             * @return {void}
             */
            handleCardPayment(token) {
                this.paymentToken = token.id

                this.submitOrder()
            },
            /**
             * Handle a payment made by a invoice provider.
             *
             * @param  {Object} token
             * @return {void}
             */
            handleInvoicePayment(token) {
                this.paymentToken = token

                this.submitOrder()
            },
            /**
             * Handle the submission of an order and payment if necessary
             *
             * @return  {void}
             */
            submitOrder() {
                if (this.paymentToken === null && this.orderAmount > 0 && this.paymentMethod === 'card') {
                    alert('No payment token received. Try again.')

                    return
                }

                axios.post(route('orders.create'), {
                    description: this.variableOrderName,
                    type: this.orderType,
                    cost: this.orderAmount,
                    quantity: this.productOrderQuantity,
                    product: this.selectedProductId,
                    account: this.account.id,
                    paymentToken: this.paymentToken,
                    attribution: this.selectedAttribution,
                    paymentMethod: this.paymentMethod,
                    tax_rate: this.taxRate.rate,
                })
                    .then(response => {
                        this.$bus.$emit('account.reload')
                        this.$bus.$emit('alert.success', 'Order has been processed successfully')
                        this.closeView()
                    })
                    .catch(e => {
                        alert('API error!')
                        console.error(e)
                    })
            },

            /**
             * Return the default tax code for this Order.
             *
             * @return
             */
            getDefaultTaxRate() {
                return this.account.tax_rate ?
                    this.$store.state.taxRates.find(el => el.rate === this.account.tax_rate.tax_rate) :
                    this.$store.state.defaultTaxRate
            },

            /**
             * Handle an [Escape] keypress
             *
             * @return {void}
             */
            onEscapeKeypress() {
                this.closeView()
            },
        }
    }
</script>

<style lang="less">

</style>
